import { createServerFn } from '@tanstack/react-start'
import z from 'zod'
import { redirect } from '@tanstack/react-router'
import { createAdminClient, createJwtClient, createSessionClient } from '../lib/appwrite'
import { Account, AppwriteException, Client, ID } from 'node-appwrite'
import {
  deleteCookie,
  getCookie,
  setCookie,
  setResponseStatus,
  getRequestHeader,
} from '@tanstack/react-start/server'

export const getAppwriteSessionFn = createServerFn({ method: 'GET' }).handler(
  async () => {
    const sessionId = getCookie(`appwrite-session-id`)
    const sessionSecret = getCookie(`appwrite-session-secret`)

    if (!sessionId && !sessionSecret) {
      return null
    }

    return {
      id: sessionId || null,
      secret: sessionSecret || null,
    }
  },
)

export const getAppwriteJwtFn = createServerFn({ method: 'GET' }).handler(
  async () => {
    const jwt = getCookie(`appwrite-jwt`)

    if (!jwt) {
      return null
    }

    return jwt
  },
)

const setAppwriteSessionCookiesSchema = z.object({
  id: z.string(),
  secret: z.string(),
  expires: z.string().optional(), // ISO 8601 format string from Appwrite session.expire
})

export const setAppwriteSessionCookiesFn = createServerFn({ method: 'POST' })
  .inputValidator(setAppwriteSessionCookiesSchema)
  .handler(
    async ({
      data,
    }: {
      data: z.infer<typeof setAppwriteSessionCookiesSchema>
    }) => {
      const { id, secret, expires } = data
      const isProd = process.env.NODE_ENV === 'production'

      // Calculate maxAge in seconds (default to 30 days if no expiration provided)
      // Appwrite expire is always an ISO 8601 format string (e.g., "2020-10-15T06:38:00.000+00:00")
      let maxAge = 30 * 24 * 60 * 60 // Default: 30 days in seconds
      if (expires) {
        const expireTime = Math.floor(new Date(expires).getTime() / 1000)
        const now = Math.floor(Date.now() / 1000)
        maxAge = Math.max(0, expireTime - now)
      }

      // Set cookies with explicit expiration
      setCookie(`appwrite-session-secret`, secret, {
        httpOnly: true,
        secure: isProd,
        sameSite: isProd ? 'none' : 'lax',
        maxAge,
        path: '/',
      })

      setCookie(`appwrite-session-id`, id, {
        httpOnly: true,
        secure: isProd,
        sameSite: isProd ? 'none' : 'lax',
        maxAge,
        path: '/',
      })
    },
  )

const setAppwriteJwtCookieSchema = z.object({
  jwt: z.string().min(1, 'JWT is required'),
  expires: z.string().optional(), // ISO 8601 format string if available
})

export const setAppwriteJwtCookieFn = createServerFn({ method: 'POST' })
  .inputValidator(setAppwriteJwtCookieSchema)
  .handler(
    async ({
      data,
    }: {
      data: z.infer<typeof setAppwriteJwtCookieSchema>
    }) => {
      const { jwt, expires } = data
      const isProd = process.env.NODE_ENV === 'production'

      let maxAge = 15 * 60 // Default JWT lifetime: 15 minutes
      if (expires) {
        const expireTime = Math.floor(new Date(expires).getTime() / 1000)
        const now = Math.floor(Date.now() / 1000)
        maxAge = Math.max(0, expireTime - now)
      }

      setCookie(`appwrite-jwt`, jwt, {
        httpOnly: true,
        secure: isProd,
        sameSite: isProd ? 'none' : 'lax',
        maxAge,
        path: '/',
      })
    },
  )

const signUpInSchema = z.object({
  email: z.email('Please enter a valid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  redirect: z.string().optional(),
})

export const signUpFn = createServerFn({ method: 'POST' })
  .inputValidator(signUpInSchema)
  .handler(async ({ data }) => {
    const { email, password, redirect: redirectUrl } = data
    const { account } = createAdminClient()

    try {
      await account.create({ userId: ID.unique(), email, password })
      const session = await account.createEmailPasswordSession({
        email,
        password,
      })
      await setAppwriteSessionCookiesFn({
        data: {
          id: session.$id,
          secret: session.secret,
          expires: session.expire || undefined, // ISO 8601 format string
        },
      })
    } catch (_error) {
      const error = _error as AppwriteException
      setResponseStatus(error.code)
      throw {
        message: error.message,
        status: error.code,
      }
    }

    if (redirectUrl) {
      throw redirect({ to: redirectUrl })
    } else {
      throw redirect({ to: '/' })
    }
  })

export const signInFn = createServerFn({ method: 'POST' })
  .inputValidator(signUpInSchema)
  .handler(async ({ data }) => {
    const { email, password, redirect: redirectUrl } = data

    try {
      const { account } = createAdminClient()
      const session = await account.createEmailPasswordSession({
        email,
        password,
      })
      await setAppwriteSessionCookiesFn({
        data: {
          id: session.$id,
          secret: session.secret,
          expires: session.expire || undefined, // ISO 8601 format string
        },
      })
    } catch (_error) {
      const error = _error as AppwriteException
      setResponseStatus(error.code)
      throw {
        message: error.message,
        status: error.code,
      }
    }

    if (redirectUrl) {
      throw redirect({ to: redirectUrl })
    } else {
      throw redirect({ to: '/' })
    }
  })

export const signOutFn = createServerFn({ method: 'GET' }).handler(async () => {
  try {
    const session = await getAppwriteSessionFn()

    if (session?.id || session?.secret) {
      const sessionValue = session?.id || session?.secret || ''
      const client = await createSessionClient(sessionValue)
      // Delete the session on Appwrite server
      await client.account.deleteSession({ sessionId: 'current' })
    }
  } catch (error) {
    // Even if session deletion fails, we still want to clear local cookies
    console.error('Error deleting session:', error)
  } finally {
    // Always delete the cookies
    clearAuthCookies()
  }
})

type AppwriteHttpError = {
  status: number
  message: string
}

const appwritePublicRequest = async <TResponse>(
  path: string,
  body: Record<string, unknown>,
  method: 'POST' | 'PUT' = 'POST',
): Promise<TResponse> => {
  const endpoint = process.env.APPWRITE_ENDPOINT
  if (!endpoint) {
    throw new Error('APPWRITE_ENDPOINT is not set')
  }

  const projectId = process.env.APPWRITE_PROJECT_ID
  if (!projectId) {
    throw new Error('APPWRITE_PROJECT_ID is not set')
  }

  const response = await fetch(`${endpoint}${path}`, {
    method,
    headers: {
      'Content-Type': 'application/json',
      'X-Appwrite-Project': projectId,
    },
    body: JSON.stringify(body),
  })

  const data = (await response.json().catch(() => ({}))) as Record<
    string,
    unknown
  >

  if (!response.ok) {
    throw {
      status: (data.code as number) || response.status,
      message: (data.message as string) || 'Appwrite request failed',
    } satisfies AppwriteHttpError
  }

  return data as TResponse
}

export const authMiddleware = createServerFn({ method: 'GET' }).handler(
  async () => {
    const currentUser = await getCurrentUser()

    return {
      currentUser,
    }
  },
)

export const checkAuthStatusFn = createServerFn({ method: 'GET' }).handler(
  async () => {
    const session = await getAppwriteSessionFn()
    const jwt = await getAppwriteJwtFn()
    const currentUser = await getCurrentUser()

    return {
      hasSessionId: Boolean(session?.id),
      hasSessionSecret: Boolean(session?.secret),
      hasJwt: Boolean(jwt),
      email: currentUser?.email || null,
    }
  },
)

const magicLinkSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
  redirect: z.string().optional(),
})

export const sendMagicLinkFn = createServerFn({ method: 'POST' })
  .inputValidator(magicLinkSchema)
  .handler(async ({ data }) => {
    const { email, redirect: redirectUrl } = data

    try {
      const origin = getRequestHeader('origin')
      if (!origin) {
        throw new Error('Missing origin header')
      }

      const callbackUrl = new URL(`${origin}/callback`)
      if (redirectUrl) {
        callbackUrl.searchParams.set('redirect', redirectUrl)
      }

      await appwritePublicRequest('/account/tokens/magic-url', {
        userId: ID.unique(),
        email,
        url: callbackUrl.toString(),
      })

      return {
        success: true,
        message: 'Magic link sent successfully',
      }
    } catch (_error) {
      const error = _error as AppwriteHttpError
      setResponseStatus(error.status || 500)
      throw {
        message: error.message,
        status: error.status || 500,
      }
    }
  })

const tokenSessionSchema = z.object({
  userId: z.string().min(1, 'User ID is required'),
  secret: z.string().min(1, 'Secret is required'),
  redirect: z.string().optional(),
})

export const createSessionFromTokenFn = createServerFn({ method: 'POST' })
  .inputValidator(tokenSessionSchema)
  .handler(async ({ data }) => {
    const { userId, secret, redirect: redirectUrl } = data

    try {
      const endpoint = process.env.APPWRITE_ENDPOINT
      if (!endpoint) {
        throw new Error('APPWRITE_ENDPOINT is not set')
      }

      const projectId = process.env.APPWRITE_PROJECT_ID
      if (!projectId) {
        throw new Error('APPWRITE_PROJECT_ID is not set')
      }

      const client = new Client().setEndpoint(endpoint).setProject(projectId)
      const account = new Account(client)

      const session = await account.createSession({ userId, secret })

      await setAppwriteSessionCookiesFn({
        data: {
          id: session.$id,
          secret: session.$id,
          expires: session.expire || undefined,
        },
      })
    } catch (_error) {
      const error = _error as AppwriteHttpError
      setResponseStatus(error.status || 500)
      throw {
        message: error.message,
        status: error.status || 500,
      }
    }

    return {
      success: true,
      redirectTo: redirectUrl || '/',
    }
  })

const clearAuthCookies = () => {
  deleteCookie(`appwrite-session-secret`)
  deleteCookie(`appwrite-session-id`)
  deleteCookie(`appwrite-jwt`)
  deleteCookie(`a_session_${process.env.APPWRITE_PROJECT_ID}`)
}

export const getCurrentUser = createServerFn({ method: 'GET' }).handler(
  async () => {
    const session = await getAppwriteSessionFn()
    const jwt = await getAppwriteJwtFn()

    if (!session?.id && !session?.secret && !jwt) {
      return null
    }

    try {
      if (jwt) {
        const client = await createJwtClient(jwt)
        const currentUser = await client.account.get()
        return currentUser
      }

      const primarySession = session?.id || session?.secret || ''
      const client = await createSessionClient(primarySession)
      const currentUser = await client.account.get()
      return currentUser
    } catch (_error) {
      const error = _error as AppwriteException
      if (error.code !== 401) {
        return null
      }

      if (jwt) {
        clearAuthCookies()
        return null
      }

      // Fallback: try the secret if the ID didn't work (or vice versa)
      if (session?.id && session?.secret && session.id !== session.secret) {
        try {
          const fallbackValue = session.secret
          const fallbackClient = await createSessionClient(fallbackValue)
          const currentUser = await fallbackClient.account.get()
          return currentUser
        } catch (fallbackError) {
          const fallbackAppwriteError = fallbackError as AppwriteException
          if (fallbackAppwriteError.code === 401) {
            clearAuthCookies()
          }
          return null
        }
      }

      clearAuthCookies()
      return null
    }
  },
)

const forgotPasswordSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

export const forgotPasswordFn = createServerFn({ method: 'POST' })
  .inputValidator(forgotPasswordSchema)
  .handler(async ({ data }) => {
    const { email } = data
    const { account } = createAdminClient()

    try {
      // Get the base URL from the origin header (includes protocol)
      const origin = getRequestHeader('origin')
      if (!origin) {
        throw new Error('Missing origin header')
      }
      const resetUrl = `${origin}/reset-password`

      await account.createRecovery({ email, url: resetUrl })

      return {
        success: true,
        message: 'Password recovery email sent successfully',
      }
    } catch (_error) {
      const error = _error as AppwriteException
      setResponseStatus(error.code)
      throw {
        message: error.message,
        status: error.code,
      }
    }
  })

const resetPasswordSchema = z.object({
  userId: z.string().min(1, 'User ID is required'),
  secret: z.string().min(1, 'Secret is required'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(8, 'Password must be at least 8 characters'),
})

export const resetPasswordFn = createServerFn({ method: 'POST' })
  .inputValidator(resetPasswordSchema)
  .handler(async ({ data }) => {
    const { userId, secret, password, confirmPassword } = data

    if (password !== confirmPassword) {
      setResponseStatus(400)
      throw {
        message: 'Passwords do not match',
        status: 400,
      }
    }

    try {
      const { account } = createAdminClient()
      await account.updateRecovery({
        userId,
        secret,
        password,
      })

      return {
        success: true,
        message: 'Password reset successfully',
      }
    } catch (_error) {
      const error = _error as AppwriteException
      setResponseStatus(error.code)
      throw {
        message: error.message,
        status: error.code,
      }
    }
  })
